// The only place this app talks to the API.
//
// Everything a screen can do to the backend goes through one function, for the
// same reason the backend routes everything through tenantRoute(): a rule
// enforced in one place is a rule, and a rule repeated in twelve fetch calls is
// a habit that will be broken by the thirteenth.
//
// What this file enforces:
//
//   * The bearer token is attached here and nowhere else, so no screen has to
//     hold it and none of them can forget it.
//   * Every mutation carries an Idempotency-Key. The API requires one and
//     answers 422 without it; generating it here means a double-clicked button
//     sends the SAME key and gets the first answer back, instead of a second
//     effect. The key is generated per call site invocation, not per retry —
//     see `mutate`.
//   * The error taxonomy becomes one typed error, so a screen distinguishes 409
//     from 404 without reading status codes, and a 401 can log the session out
//     from one place.
//
// What this file deliberately does NOT do: interpret, retry or reorder. An
// external write returns 202 and an operation id; whether the effect exists is
// a question only GET /operations/{id} answers, and this client has no opinion
// about it.

import { csrfToken } from '../session.ts';
import type { ApiErrorBody } from './types.ts';

/** The API's base URL, e.g. https://api.example.eu. No trailing slash. */
const BASE_URL: string = (import.meta.env['VITE_API_BASE_URL'] as string | undefined) ?? 'http://127.0.0.1:8080';

export class ApiError extends Error {
  readonly status: number;
  readonly code: ApiErrorBody['code'] | 'transport_error';
  readonly correlationId: string | null;
  readonly fieldErrors: readonly { field: string; code: string }[];

  constructor(
    status: number,
    body: Omit<Partial<ApiErrorBody>, 'code'> & { code: ApiErrorBody['code'] | 'transport_error' },
  ) {
    super(body.message ?? `request failed with ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.correlationId = body.correlationId ?? null;
    this.fieldErrors = body.fieldErrors ?? [];
  }

  /** The caller asked to act on a revision that is no longer current. */
  get isStale(): boolean {
    return this.status === 409;
  }

  /** Missing OR inaccessible. The API does not tell the two apart, and neither do we. */
  get isAbsent(): boolean {
    return this.status === 404;
  }
}

export interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PATCH';
  readonly token: string;
  readonly body?: unknown;
  /** Required for a mutation; the API refuses one without it. */
  readonly idempotencyKey?: string;
  readonly signal?: AbortSignal;
}

async function parseError(response: Response): Promise<ApiError> {
  try {
    const body = (await response.json()) as ApiErrorBody;
    if (typeof body?.code === 'string') return new ApiError(response.status, body);
  } catch {
    // An error response that is not the taxonomy — a proxy's HTML page, say.
  }
  return new ApiError(response.status, { code: 'internal_error' });
}

export async function request<T>(path: string, options: RequestOptions): Promise<T> {
  const headers: Record<string, string> = {
    authorization: `Bearer ${options.token}`,
    accept: 'application/json',
  };
  if (options.body !== undefined) headers['content-type'] = 'application/json';
  if (options.idempotencyKey !== undefined) headers['idempotency-key'] = options.idempotencyKey;
  // The double submit, when a deployment has cookie sessions. Absent here means
  // this page has no CSRF cookie, which means there is no cookie session
  // either, and the API will see an ordinary bearer request. See session.ts.
  const csrf = csrfToken();
  if (csrf !== null) headers['x-csrf-token'] = csrf;

  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method: options.method ?? 'GET',
      headers,
      // Required for a cookie session to reach the API at all: the app and the
      // API are separate origins, and fetch's default omits credentials
      // cross-origin. Safe to send unconditionally — the API reflects an origin
      // only from its allowlist and never `*`, so a page that is not on the
      // list cannot read the answer whatever it sends.
      credentials: 'include',
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
      ...(options.signal === undefined ? {} : { signal: options.signal }),
    });
  } catch {
    // The API is unreachable, or the browser refused the response because this
    // origin is not on the API's allow-list. Both are configuration, and both
    // must read as "we do not know" rather than as an answer — in particular,
    // never as "there is nothing there".
    throw new ApiError(0, {
      code: 'transport_error',
      message: 'The service could not be reached.',
    });
  }

  if (response.status === 204) return undefined as T;
  if (!response.ok) throw await parseError(response);
  return (await response.json()) as T;
}

/**
 * A request that carries NO session: sign-in and redeeming an invitation, the
 * two public routes this app calls.
 *
 * Separate from `request` rather than `request` with an optional token, so a
 * screen cannot forget the token and quietly make an anonymous call, and so
 * these two never send an ambient cookie either (`credentials: 'omit'`): a
 * sign-in must be decided by what the person typed and nothing else.
 */
export async function publicRequest<T>(path: string, body: unknown): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method: 'POST',
      headers: { accept: 'application/json', 'content-type': 'application/json' },
      credentials: 'omit',
      body: JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, {
      code: 'transport_error',
      message: 'The service could not be reached.',
    });
  }
  if (!response.ok) throw await parseError(response);
  return (await response.json()) as T;
}

/** What came back from a viewer URL: the bytes, and what the server says they are. */
export interface SourceBytes {
  readonly bytes: Uint8Array;
  readonly mediaType: string;
}

/**
 * The stored source bytes behind an issued viewer URL.
 *
 * A separate function from `request` because the answer is not JSON, and the
 * difference is worth a name rather than a flag: everything else this app reads
 * is a response shaped by a schema, and this is a customer's document.
 *
 * Three things it does that `request` does not:
 *
 *   * It resolves the URL the API gave it against the API's base rather than
 *     assuming a path. `POST .../source-url` returns a root-relative URL today
 *     and would return an absolute one on a provider bucket; `new URL(url,
 *     base)` is the same line for both, so a bucket needs no change here.
 *   * It sends the bearer token. The URL is not a bearer credential — the API
 *     refuses it without a session — and the reason it is fetched rather than
 *     put in an <img> or an <iframe> src is that the API's own security headers
 *     forbid this origin being rendered in. So the app fetches the bytes and
 *     renders them itself, which is also what makes a sandboxed viewer possible.
 *   * It returns the media type the SERVER decided, never a guess from the URL.
 *     packages/storage sniffs the bytes and refuses to believe what a sender
 *     declared; picking a renderer from a filename here would undo that.
 */
export async function requestSource(
  url: string,
  options: { readonly token: string; readonly signal?: AbortSignal },
): Promise<SourceBytes> {
  let response: Response;
  try {
    response = await fetch(new URL(url, BASE_URL).toString(), {
      headers: { authorization: `Bearer ${options.token}` },
      ...(options.signal === undefined ? {} : { signal: options.signal }),
    });
  } catch {
    throw new ApiError(0, {
      code: 'transport_error',
      message: 'The service could not be reached.',
    });
  }

  if (!response.ok) throw await parseError(response);
  const buffer = await response.arrayBuffer();
  return {
    bytes: new Uint8Array(buffer),
    // Without a content type there is nothing honest to render: a default here
    // would be this function deciding what a customer's document is.
    mediaType: (response.headers.get('content-type') ?? '').split(';')[0]?.trim() ?? '',
  };
}

/**
 * A stable key for one user intention.
 *
 * Generated once per call and reused if the caller retries, which is what makes
 * a retry safe: contracts/http-api.md says a reused key with the same content
 * returns the first answer, and with different content returns 409. A key
 * regenerated on every attempt would turn a double click into two operations.
 */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

export function tenantPath(tenantId: string, path: string): string {
  return `/v1/tenants/${encodeURIComponent(tenantId)}${path}`;
}
